"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFetchData } from "../../../hooks/useFetch";
import { useViewportMaxHeight } from "../../../hooks/useViewportMaxHeight";
import { isSuccess } from "../../../models/response/base-response";
import { ApiConfig, API_ORDER_REPORT_ROUTER } from "../../../services/api-config";
import BranchService, { Branch } from "../../branch/services/branch-service";
import DateInputCustom from "../../../components/custom/date-input/date-input-custom";
import EmptyState from "../../../components/custom/empty-state";
import { showToast } from "../../../components/custom/custom-toast";
import { formatCurrency, removeVietnameseFromString } from "../../../utils/helpers";
import { downloadOrderServiceExcel } from "../../order-list/utils/download-excel";
import ServiceTable, {
  CustomerRow,
  EMPTY_SUMMARY,
  SalesOutputReport,
  TABLE_CLASS,
  TD,
  TD_NUM,
  TF,
  TH,
  THEAD_CLASS,
  formatPercent,
} from "../components/service-table";
import CustomerServicesModal from "../components/customer-services-modal";

/**
 * Báo cáo xuất bán — doanh thu theo dịch vụ (SL, doanh thu, giá TB), theo chi nhánh chủ đơn và theo khách hàng.
 * BE: order service `SalesOutputReportService`. Chỉ đơn Hoàn thành (theo ngày hoàn thành), không gồm đơn giặt
 * lại, giới hạn theo chi nhánh của người xem.
 */

type Report = SalesOutputReport;
type Tab = "service" | "branch" | "customer";

function extractList(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw?.list && Array.isArray(raw.list)) return raw.list;
  if (raw?.data && Array.isArray(raw.data)) return raw.data;
  return [];
}

// DateInputCustom dùng dd/MM/yyyy; BE nhận YYYY-MM-DD → convert ở ranh giới.
const toDMY = (ymd: string) => {
  const [y, m, d] = (ymd || "").split("-");
  return d && m && y ? `${d}/${m}/${y}` : "";
};
const toYMD = (dmy: string) => {
  const [d, m, y] = (dmy || "").split("/");
  return d && m && y ? `${y}-${m}-${d}` : "";
};

const getDefaultDates = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
  return { from: fmt(firstDay), to: fmt(now) };
};

const normalize = (v: string) => removeVietnameseFromString(v || "").toLowerCase();

const VIEW_BUTTON =
  "whitespace-nowrap rounded-lg border border-blue-200 px-2.5 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50";

const SalesOutputReportPage: React.FC = () => {
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.from);
  const [endDate, setEndDate] = useState(defaults.to);
  const [selectedBranchId, setSelectedBranchId] = useState<number | "">("");
  const [branches, setBranches] = useState<Branch[]>([]);
  const [report, setReport] = useState<Report | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState<Tab>("service");
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const [detailCustomer, setDetailCustomer] = useState<CustomerRow | null>(null);

  const { ref: frameRef, maxHeight } = useViewportMaxHeight<HTMLDivElement>(40);

  const { request: fetchReport } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
  // Chỉ chi nhánh user được phụ trách (BE cũng giới hạn lại theo token).
  const { fetch: listBranches } = BranchService.myBranches();
  const fetchRef = useRef(fetchReport);
  const branchRef = useRef(listBranches);
  useEffect(() => { fetchRef.current = fetchReport; }, [fetchReport]);
  useEffect(() => { branchRef.current = listBranches; }, [listBranches]);

  useEffect(() => {
    (async () => {
      const res = await branchRef.current();
      if (res && isSuccess(res)) setBranches(extractList(res.data));
    })();
  }, []);

  const loadReport = useCallback(async () => {
    if (!startDate || !endDate) return;
    setIsLoading(true);
    try {
      const params: Record<string, unknown> = { from_date: startDate, to_date: endDate };
      if (selectedBranchId) params.branch_id = selectedBranchId;
      const res = await fetchRef.current({ endPoint: API_ORDER_REPORT_ROUTER.API_SALES_OUTPUT_REPORT, data: params });
      if (res && isSuccess(res)) setReport(res.data ?? null);
      else showToast({ message: (res as any)?.message || "Không tải được báo cáo", type: "error" });
    } catch (e) {
      console.error("Failed to load sales output report:", e);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, selectedBranchId]);

  useEffect(() => { void loadReport(); }, [loadReport]);

  // Chỉ còn 1 chi nhánh thì tab "Theo chi nhánh" không có ý nghĩa → quay về tab dịch vụ.
  const showBranchTab = !selectedBranchId && (report?.by_branch.length ?? 0) > 1;
  useEffect(() => { if (!showBranchTab && tab === "branch") setTab("service"); }, [showBranchTab, tab]);

  const summary = report?.summary ?? EMPTY_SUMMARY;

  const services = useMemo(() => {
    const list = report?.services ?? [];
    const q = normalize(search.trim());
    if (!q) return list;
    return list.filter((r) => [r.item_code, r.item_name, r.unit_name].some((f) => normalize(f).includes(q)));
  }, [report, search]);

  const branchRows = useMemo(() => {
    const list = report?.by_branch ?? [];
    const q = normalize(search.trim());
    if (!q) return list;
    return list.filter((r) => normalize(r.branch_name).includes(q));
  }, [report, search]);

  const customerRows = useMemo(() => {
    const list = report?.by_customer ?? [];
    const q = normalize(search.trim());
    if (!q) return list;
    return list.filter((r) =>
      [r.customer_code, r.customer_name, r.customer_phone].some((f) => normalize(f).includes(q)),
    );
  }, [report, search]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params: Record<string, unknown> = { from_date: startDate, to_date: endDate };
      if (selectedBranchId) params.branch_id = selectedBranchId;
      await downloadOrderServiceExcel(
        API_ORDER_REPORT_ROUTER.API_SALES_OUTPUT_REPORT_EXPORT,
        params,
        `xuat-ban_${startDate}_${endDate}.xlsx`,
      );
    } catch (e: any) {
      showToast({ message: e?.message || "Xuất Excel thất bại", type: "error" });
    } finally {
      setExporting(false);
    }
  };

  const cards = [
    { label: "Số đơn hoàn thành", value: summary.order_count.toLocaleString("en-US"), unit: "", tone: "text-gray-900" },
    { label: "Doanh thu trước chiết khấu", value: formatCurrency(summary.gross_amount), unit: "đ", tone: "text-gray-900" },
    { label: "Chiết khấu & giảm trừ", value: formatCurrency(summary.discount_amount), unit: "đ", tone: "text-orange-600" },
    { label: "Doanh thu thuần", value: formatCurrency(summary.net_amount), unit: "đ", tone: "text-blue-700" },
  ];

  const tabButton = (key: Tab, label: string, count: number) => (
    <button
      key={key}
      type="button"
      onClick={() => setTab(key)}
      className={`border-b-2 pb-1 text-sm font-semibold transition-colors ${
        tab === key ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
    >
      {label} <span className="ml-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-700">{count}</span>
    </button>
  );

  const renderCustomerTable = () => (
    <table className={TABLE_CLASS}>
      <thead className={THEAD_CLASS}>
        <tr>
          <th className={`${TH} w-14`}>STT</th>
          <th className={TH}>Mã KH</th>
          <th className={`${TH} min-w-[220px]`}>Khách hàng</th>
          <th className={TH}>SĐT</th>
          <th className={TH}>Số đơn</th>
          <th className={TH}>Trước chiết khấu (đ)</th>
          <th className={TH}>Chiết khấu & giảm trừ (đ)</th>
          <th className={TH}>Doanh thu thuần (đ)</th>
          <th className={TH}>Tỷ trọng</th>
          <th className={TH}></th>
        </tr>
      </thead>
      <tbody>
        {customerRows.map((c, i) => (
          <tr key={c.customer_id} className="hover:bg-gray-50">
            <td className={`${TD} text-center text-gray-400`}>{i + 1}</td>
            <td className={`${TD} whitespace-nowrap text-gray-600`}>{c.customer_code || "-"}</td>
            <td className={`${TD} font-medium text-gray-900`}>{c.customer_name}</td>
            <td className={`${TD} whitespace-nowrap text-gray-600`}>{c.customer_phone || "-"}</td>
            <td className={`${TD} text-center`}>{c.order_count}</td>
            <td className={TD_NUM}>{formatCurrency(c.gross_amount)}</td>
            <td className={`${TD_NUM} text-orange-600`}>{formatCurrency(c.discount_amount)}</td>
            <td className={`${TD_NUM} font-semibold text-blue-700`}>{formatCurrency(c.net_amount)}</td>
            <td className={TD_NUM}>{formatPercent(c.share)}</td>
            <td className={`${TD} text-center`}>
              <button type="button" onClick={() => setDetailCustomer(c)} className={VIEW_BUTTON}>
                Xem dịch vụ
              </button>
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={4} className={`${TF} border-t-2 border-t-blue-200`}>Tổng cộng ({report?.by_customer.length ?? 0} khách)</td>
          <td className={`${TF} border-t-2 border-t-blue-200 text-center`}>{summary.order_count}</td>
          <td className={`${TF} border-t-2 border-t-blue-200`}>{formatCurrency(summary.gross_amount)}</td>
          <td className={`${TF} border-t-2 border-t-blue-200 text-orange-700`}>{formatCurrency(summary.discount_amount)}</td>
          <td className={`${TF} border-t-2 border-t-blue-200 text-blue-900`}>{formatCurrency(summary.net_amount)}</td>
          <td className={`${TF} border-t-2 border-t-blue-200`}>100.00%</td>
          <td className={`${TF} border-t-2 border-t-blue-200 border-r-0`}></td>
        </tr>
      </tfoot>
    </table>
  );

  const renderBranchTable = () => (
    <table className={TABLE_CLASS}>
      <thead className={THEAD_CLASS}>
        <tr>
          <th className={`${TH} w-14`}>STT</th>
          <th className={`${TH} min-w-[220px]`}>Chi nhánh</th>
          <th className={TH}>Số đơn</th>
          <th className={TH}>Trước chiết khấu (đ)</th>
          <th className={TH}>Chiết khấu & giảm trừ (đ)</th>
          <th className={TH}>Doanh thu thuần (đ)</th>
          <th className={TH}>Tỷ trọng</th>
          <th className={TH}></th>
        </tr>
      </thead>
      <tbody>
        {branchRows.map((b, i) => (
          <tr key={b.branch_id} className="hover:bg-gray-50">
            <td className={`${TD} text-center text-gray-400`}>{i + 1}</td>
            <td className={`${TD} font-medium text-gray-900`}>{b.branch_name}</td>
            <td className={`${TD} text-center`}>{b.order_count}</td>
            <td className={TD_NUM}>{formatCurrency(b.gross_amount)}</td>
            <td className={`${TD_NUM} text-orange-600`}>{formatCurrency(b.discount_amount)}</td>
            <td className={`${TD_NUM} font-semibold text-blue-700`}>{formatCurrency(b.net_amount)}</td>
            <td className={TD_NUM}>{formatPercent(b.share)}</td>
            <td className={`${TD} text-center`}>
              <button
                type="button"
                onClick={() => { setSelectedBranchId(b.branch_id); setSearch(""); }}
                className={VIEW_BUTTON}
              >
                Xem dịch vụ
              </button>
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={2} className={`${TF} border-t-2 border-t-blue-200`}>Tổng cộng ({report?.by_branch.length ?? 0} chi nhánh)</td>
          <td className={`${TF} border-t-2 border-t-blue-200 text-center`}>{summary.order_count}</td>
          <td className={`${TF} border-t-2 border-t-blue-200`}>{formatCurrency(summary.gross_amount)}</td>
          <td className={`${TF} border-t-2 border-t-blue-200 text-orange-700`}>{formatCurrency(summary.discount_amount)}</td>
          <td className={`${TF} border-t-2 border-t-blue-200 text-blue-900`}>{formatCurrency(summary.net_amount)}</td>
          <td className={`${TF} border-t-2 border-t-blue-200`}>100.00%</td>
          <td className={`${TF} border-t-2 border-t-blue-200 border-r-0`}></td>
        </tr>
      </tfoot>
    </table>
  );

  const rowsOfTab =
    tab === "service" ? services.length : tab === "branch" ? branchRows.length : customerRows.length;
  const searchPlaceholder =
    tab === "service"
      ? "Tìm mã, tên dịch vụ, đơn vị tính..."
      : tab === "branch"
        ? "Tìm chi nhánh..."
        : "Tìm mã, tên, SĐT khách hàng...";

  return (
    <div className="space-y-6">
      {/* Header + bộ lọc */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Báo cáo xuất bán</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Doanh thu theo dịch vụ, chi nhánh và khách hàng · đơn hoàn thành trong kỳ, không gồm đơn giặt lại
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportExcel}
              disabled={exporting || !startDate || !endDate}
              className="flex items-center gap-2 h-10 px-4 bg-green-600 text-white text-sm font-medium rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {exporting ? "Đang xuất..." : "Xuất Excel"}
            </button>
            <button
              onClick={loadReport}
              disabled={isLoading}
              aria-label="Tải lại"
              className="flex items-center justify-center w-10 h-10 bg-white border border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <svg className={`w-5 h-5 text-gray-600 ${isLoading ? "animate-spin" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Ngày hoàn thành đơn</label>
            <div className="flex items-center gap-2">
              <DateInputCustom
                type="date"
                value={toDMY(startDate)}
                maxDate={toDMY(endDate) || undefined}
                onChange={(d) => setStartDate(toYMD(d ?? ""))}
                className="w-[150px]"
                placeholder="Từ ngày"
              />
              <span className="text-slate-400">→</span>
              <DateInputCustom
                type="date"
                value={toDMY(endDate)}
                minDate={toDMY(startDate) || undefined}
                onChange={(d) => setEndDate(toYMD(d ?? ""))}
                className="w-[150px]"
                placeholder="Đến ngày"
              />
            </div>
          </div>
          <div className="w-full sm:w-[280px]">
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Chi nhánh chủ đơn</label>
            <select
              value={selectedBranchId}
              onChange={(e) => setSelectedBranchId(e.target.value ? Number(e.target.value) : "")}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white transition-colors"
            >
              <option value="">Tất cả chi nhánh phụ trách</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Thẻ tổng */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <p className="text-sm font-medium text-gray-500">{c.label}</p>
            <p className={`text-2xl font-bold mt-1 ${c.tone}`}>
              {c.value} {c.unit && <span className="text-sm font-normal text-gray-400">{c.unit}</span>}
            </p>
          </div>
        ))}
      </div>

      {/* Bảng */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-lg bg-slate-50 px-6 py-3">
          <div className="flex flex-wrap gap-6">
            {tabButton("service", "Theo dịch vụ", services.length)}
            {showBranchTab && tabButton("branch", "Theo chi nhánh", branchRows.length)}
            {tabButton("customer", "Theo khách hàng", customerRows.length)}
          </div>
          <div className="relative w-full sm:w-[300px]">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
              </svg>
            </div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full rounded-xl border border-gray-200 bg-white pl-9 pr-4 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all"
            />
          </div>
        </div>

        <div ref={frameRef} style={{ maxHeight }} className="mt-4 overflow-auto rounded-lg border border-gray-200 bg-white">
          {isLoading && !report ? (
            <div className="py-16 text-center text-sm text-gray-400">Đang tải...</div>
          ) : rowsOfTab === 0 ? (
            <EmptyState
              message={search.trim() ? "Không tìm thấy kết quả" : "Chưa có doanh thu"}
              description={
                search.trim()
                  ? "Không có dòng nào khớp từ khoá tìm kiếm."
                  : "Không có đơn hoàn thành nào trong khoảng thời gian và chi nhánh đã chọn."
              }
            />
          ) : (
            <div className={isLoading ? "opacity-60 transition-opacity" : "transition-opacity"}>
              {tab === "service" ? (
                <ServiceTable rows={services} summary={summary} />
              ) : tab === "branch" ? (
                renderBranchTable()
              ) : (
                renderCustomerTable()
              )}
            </div>
          )}
        </div>
      </div>

      {detailCustomer && (
        <CustomerServicesModal
          customer={detailCustomer}
          startDate={startDate}
          endDate={endDate}
          branchId={selectedBranchId}
          onClose={() => setDetailCustomer(null)}
        />
      )}
    </div>
  );
};

export default SalesOutputReportPage;
